import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import CollectionsIcon from "@material-ui/icons/Collections";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

import apiClient from "../../../../../../auth/apiClient";
import InstallationPhotoService from "../../../../../../services/InstallationPhotoService";
import { useConfirmationDialog } from "../../../../../../hooks/useConfirmationDialog";
import CameraCapture from "./CameraCapture";
import PhotoList from "./PhotoList";
import PhotoViewer from "./PhotoViewer";
import {
  FILE_INPUT_ACCEPT,
  MAX_PHOTOS,
  extensionFromMimeType,
  readFileAsDataUrl,
  stripDataUrlPrefix,
  validateFile,
} from "./constants";
import { useStyles } from "./styles";

const service = new InstallationPhotoService(apiClient);

/**
 * Container for the technician installation photos UI shown from the device
 * Details tab. Owns local state (photos, selected photo, upload/delete status)
 * and orchestrates the camera, gallery, list, and viewer sub-components.
 *
 * The backend endpoints are not implemented yet; this talks to the
 * `installation-photos` contract via InstallationPhotoService and degrades
 * gracefully (shows an error, keeps state) when calls fail.
 */
const InstallationPhotos = ({ entityID, deviceId, onClose }) => {
  const classes = useStyles();
  const { showDialog } = useConfirmationDialog();
  const fileInputRef = useRef(null);

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const targetDeviceId = deviceId || entityID;
  const limitReached = photos.length >= MAX_PHOTOS;

  useEffect(() => {
    window.history.pushState({ installationPhotos: true }, "");

    const handlePopState = () => {
      if (onClose) onClose();
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await service.listByDevice(targetDeviceId);
      setPhotos(Array.isArray(data) ? data : []);
    } catch (e) {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [targetDeviceId]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const uploadImage = async (dataUrl, mimeType) => {
    setUploading(true);
    setUploadError(null);
    try {
      await service.upload({
        deviceId: targetDeviceId,
        entityId: entityID,
        encodedImage: stripDataUrlPrefix(dataUrl),
        imageExtension: extensionFromMimeType(mimeType),
      });
      setCameraOpen(false);
      await loadPhotos();
      return true;
    } catch (e) {
      setUploadError("Upload failed. Please try again.");
      return false;
    } finally {
      setUploading(false);
    }
  };

  // Camera capture always produces a JPEG data URL.
  const handleCameraUpload = (dataUrl) => uploadImage(dataUrl, "image/jpeg");

  const handleGallerySelect = async (event) => {
    const file = event.target.files && event.target.files[0];
    // Reset the input so the same file can be re-selected later.
    event.target.value = "";
    if (!file) return;

    const { valid, error } = validateFile(file);
    if (!valid) {
      setUploadError(error);
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      await uploadImage(dataUrl, file.type);
    } catch (e) {
      setUploadError("Could not read the selected file.");
    }
  };

  const handleDelete = async (photo) => {
    // Shared yes/no confirmation dialog (Req 5.5, 5.6). showDialog resolves to
    // the clicked button name.
    const choice = await showDialog({
      title: "Delete Photo",
      message: "Are you sure you want to delete this photo?",
      buttons: [
        { name: "YES", color: "primary" },
        { name: "NO", color: "secondary" },
      ],
    });
    if (choice !== "YES") {
      return;
    }

    setDeleting(true);
    setDeleteError(null);
    try {
      await service.remove(photo.deviceId, photo.installationImageId);
      setSelectedPhoto(null);
      await loadPhotos();
    } catch (e) {
      // Keep the photo in the list, surface the error (Req 5.9 / 11.5).
      setDeleteError("Could not delete the photo. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // Blue "Details" navigation bar shown at the top of the Installation page
  // (matches the mockup's upper navigation). The back arrow goes to the
  // previous view: the device info view from the list, or the list from a
  // sub-view (camera / single-photo viewer).
  const renderHeader = (onBack) => (
    <Box className={classes.navBar} data-testid="installation-photos-header">
      <IconButton
        size="small"
        aria-label="back"
        onClick={onBack}
        className={classes.navBackButton}
        data-testid="installation-photos-back"
      >
        <ArrowBackIcon fontSize="small" />
      </IconButton>
      <Typography className={classes.navTitle} variant="subtitle1">
        Details
      </Typography>
    </Box>
  );

  // Full-screen viewer: a fixed overlay covering the whole viewport (mobile
  // and desktop), rendered on top of everything.
  if (selectedPhoto) {
    const backToList = () => {
      setSelectedPhoto(null);
      setDeleteError(null);
    };
    return (
      <PhotoViewer
        photo={selectedPhoto}
        deleting={deleting}
        deleteError={deleteError}
        onDelete={handleDelete}
        onClose={backToList}
      />
    );
  }

  if (cameraOpen) {
    const backToList = () => {
      setCameraOpen(false);
      setUploadError(null);
    };
    return (
      <Box className={classes.page} data-testid="installation-photos">
        <Box className={classes.pageBody}>
          <CameraCapture
            uploading={uploading}
            uploadError={uploadError}
            onUpload={handleCameraUpload}
            onClose={backToList}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.page} data-testid="installation-photos">
      <Box className={classes.pageBody}>
        <Box className={classes.actionsBar}>
          {/* Tooltip explains why the actions are disabled at the photo limit.
              The <span> wrapper is required for MUI tooltips on disabled buttons. */}
          <Tooltip
            title={
              limitReached
                ? `Maximum of ${MAX_PHOTOS} photos reached. Delete a photo to add another.`
                : ""
            }
            arrow
          >
            <span className={classes.actionButtonWrapper}>
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                disabled={limitReached || uploading}
                onClick={() => {
                  setUploadError(null);
                  setCameraOpen(true);
                }}
                className={classes.actionButton}
                data-testid="take-photo-button"
              >
                Take Photo
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              limitReached
                ? `Maximum of ${MAX_PHOTOS} photos reached. Delete a photo to add another.`
                : ""
            }
            arrow
          >
            <span className={classes.actionButtonWrapper}>
              <Button
                variant="outlined"
                startIcon={<CollectionsIcon />}
                disabled={limitReached || uploading}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                className={classes.actionButton}
                data-testid="choose-gallery-button"
              >
                Choose From Gallery
              </Button>
            </span>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            accept={FILE_INPUT_ACCEPT}
            className={classes.hiddenInput}
            onChange={handleGallerySelect}
            data-testid="gallery-file-input"
          />
        </Box>

        {uploadError && (
          <Typography
            color="error"
            variant="body2"
            className={classes.inlineError}
            data-testid="upload-error"
          >
            {uploadError}
          </Typography>
        )}

        {uploading && (
          <Box className={classes.inlineLoading} data-testid="upload-progress">
            <CircularProgress size={20} />
            <Typography variant="body2">Uploading…</Typography>
          </Box>
        )}

        {loading ? (
          <Box className={classes.inlineLoading} data-testid="list-loading">
            <CircularProgress size={24} />
          </Box>
        ) : (
          <PhotoList photos={photos} onSelect={setSelectedPhoto} />
        )}
      </Box>
    </Box>
  );
};

InstallationPhotos.propTypes = {
  entityID: PropTypes.string.isRequired,
  deviceId: PropTypes.string,
  onClose: PropTypes.func,
};

InstallationPhotos.defaultProps = {
  deviceId: null,
  onClose: null,
};

export default InstallationPhotos;
